import TextButton from 'components/Luxkit/Button/TextButton'
import DropdownSheetFooter from 'components/Luxkit/Dropdown/Sheet/DropdownSheetFooter'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { interactionStudioAPIController } from 'lib/analytics/interactionStudioUtils'
import React, { useCallback, useContext } from 'react'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferCalenderDatePickerFooterPriceDetails from './OfferCalenderDatePickerFooterPriceDetails'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import { OFFER_TYPE_VILLA } from 'constants/offer'

interface Props {
  canBook?: boolean;
  offer: App.Offer;
  selectedDay?: App.CalendarDay;
  duration: number;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView?: OfferPackageOptionView;
  interactionStudioData?: App.ISACheckoutData;
  isAnyFlightsActive?: boolean;
  userMultiItemCartEnabled?: boolean;
}

function OfferCalendarDatePickerDropdownFooter(props: Props) {
  const {
    canBook,
    offer,
    selectedDay,
    duration,
    relevantPackages,
    selectedPackageView,
    interactionStudioData,
    isAnyFlightsActive,
    userMultiItemCartEnabled = false,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const canAddToUserCart = userMultiItemCartEnabled && canBook && offer.type !== OFFER_TYPE_VILLA

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    interactionStudioAPIController('checkin', interactionStudioData)
  }, [dispatch, interactionStudioData])

  const onBookNowButtonClick = useCallback(() => {
    interactionStudioAPIController('checkin', interactionStudioData)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [interactionStudioData, onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  return <DropdownSheetFooter
    startSlot={selectedDay && <OfferCalenderDatePickerFooterPriceDetails
      offer={offer}
      duration={duration}
      selectedDay={selectedDay}
      isAnyFlightsActive={isAnyFlightsActive}
      isFromPrice={relevantPackages.length > 1}
    />}
    actionsSlot={<>
      {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
        Reset dates
      </TextButton>}
      {canAddToUserCart && <>
        {isAnyFlightsActive && <OfferSelectPackageButton
          size="large"
          data-buytype="flights"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {!isAnyFlightsActive && <OfferAddToCartButton
          size="large"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
      </>}
      {canBook && (!userMultiItemCartEnabled || !isAnyFlightsActive) && <OfferBookButton
        size="large"
        data-testid="offer-calendar-apply"
        data-buytype={isAnyFlightsActive ? 'flights' : undefined}
        packageView={selectedPackageView ?? relevantPackages[0]}
        onSubmit={onBookNowButtonClick}
      >
        Book now
      </OfferBookButton>}
      {canBook && isAgentHubMultiCartItemsEnabled && (
        <OfferBookButton
          size="large"
          packageView={selectedPackageView ?? relevantPackages[0]}
          kind="secondary"
          addToCartAndKeepBrowsing
        >
          Add to cart
        </OfferBookButton>
      )}
      {!canBook && <TextButton
        kind="primary"
        size="large"
        data-testid="offer-calendar-apply"
        onClick={onContinue}
      >
        Continue
      </TextButton>}
    </>}
  />
}

export default React.memo(OfferCalendarDatePickerDropdownFooter)
